import React, { useEffect, useState } from "react";
import { Button, Input, Message, Select, Tag, Typography } from "@arco-design/web-react";
import { IconDelete, IconEdit, IconPlus } from "@arco-design/web-react/icon";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, SaveApi, DeleteApi } from "../Api/AuthenticatedApi";
import { sequenceStatusMeta, formatDelay } from "./sequenceMeta";
import RecordNotFound from "../Components/RecordNotFound";
import { summarizeEnrollments } from "./enrollmentSummary";
import BackToReport from "../Components/BackToReport";

const { Text } = Typography;
const Option = Select.Option;

export default function EditSequence() {
  const { sequenceId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [record, setRecord] = useState(null);
  const [steps, setSteps] = useState([]);
  const [senders, setSenders] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [enrollments, setEnrollments] = useState([]);

  const authFail = () => navigate("/login");

  const loadSteps = () =>
    FetchApi(getAccessTokenSilently, `/api/sequences/${sequenceId}/steps/`,
      (data) => setSteps(Array.isArray(data) ? data : []), authFail, () => {});

  useEffect(() => {
    FetchApi(getAccessTokenSilently, `/api/sequences/${sequenceId}`,
      (data) => { setRecord(data); setLoading(false); }, authFail,
      () => { setLoading(false); setLoadFailed(true); });
    loadSteps();
    FetchApi(getAccessTokenSilently, "/api/settings/confirmed-sender-emails/",
      (data) => setSenders(Array.isArray(data) ? data : []), authFail, () => {});
    FetchApi(getAccessTokenSilently, "/api/contact-lists/",
      (data) => setLists(Array.isArray(data) ? data : []), authFail, () => {});
    FetchApi(getAccessTokenSilently, `/api/sequences/${sequenceId}/enrollments/`,
      (data) => setEnrollments(Array.isArray(data) ? data : []), authFail, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceId, getAccessTokenSilently]);

  const setField = (field, value) => setRecord((r) => ({ ...r, [field]: value }));

  const saveSettings = () => {
    SaveApi(getAccessTokenSilently, `/api/sequences/${sequenceId}`,
      (data) => { setRecord(data); Message.success("Sequence saved"); },
      authFail, (err) => Message.error((err && err.message) || "Failed to save"), record);
  };

  const deleteStep = (step) => {
    DeleteApi(getAccessTokenSilently, `/api/sequences/${sequenceId}/steps/${step.id}`,
      () => { Message.success("Step deleted"); loadSteps(); }, authFail,
      (err) => Message.error((err && err.message) || "Failed to delete step"), {});
  };

  if (loadFailed) {
    return <RecordNotFound what="sequence" backTo="/sequences" backLabel="Back to Sequences" />;
  }
  if (loading || !record) {
    return <LoadingState />;
  }

  const statusMeta = sequenceStatusMeta(record.status);

  return (
    <Content>
      <BackToReport />
      <SimpleBreadcrumbs elements={[{ name: "Sequences", path: "/sequences" }, { name: record.name }]} />

      <SummaryCard
        title={record.name}
        titleElement={<Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 16, maxWidth: 720 }}>
          <Field label="Name">
            <Input value={record.name} onChange={(v) => setField("name", v)} />
          </Field>
          <Field label="Status">
            <Select value={record.status || "draft"} onChange={(v) => setField("status", v)}>
              <Option value="draft">Draft</Option>
              <Option value="active">Active</Option>
              <Option value="paused">Paused</Option>
            </Select>
          </Field>
          <Field label="Sender">
            <Select
              placeholder="Choose a verified sender"
              value={record.sender_email_id || undefined}
              onChange={(v) => setField("sender_email_id", v)}
            >
              {senders.map((s) => (<Option key={s.id} value={s.id}>{s.email}</Option>))}
            </Select>
          </Field>
          <Field label="Trigger">
            <Select value={record.trigger_type || "manual"} onChange={(v) => setField("trigger_type", v)}>
              <Option value="manual">Manual enrollment</Option>
              <Option value="list_join">When a contact joins a list</Option>
            </Select>
          </Field>
          {record.trigger_type === "list_join" && (
            <Field label="Trigger list">
              <Select
                placeholder="Choose a list"
                value={record.trigger_list_id || undefined}
                onChange={(v) => setField("trigger_list_id", v)}
              >
                {lists.map((l) => (<Option key={l.id} value={l.id}>{l.name}</Option>))}
              </Select>
            </Field>
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={saveSettings}>Save settings</Button>
        </div>
      </SummaryCard>

      <SummaryCard title="Enrollments" subTitle="Contacts moving through this sequence">
        {(() => {
          const s = summarizeEnrollments(enrollments);
          if (s.total === 0) {
            return <Text type="secondary">No one is enrolled yet. Active list-join sequences enroll new signups automatically.</Text>;
          }
          return (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <Text style={{ marginRight: 4 }}>{s.total} total</Text>
              <Tag color="green">{s.active} active</Tag>
              <Tag color="arcoblue">{s.completed} completed</Tag>
              <Tag color="gray">{s.cancelled} cancelled</Tag>
            </div>
          );
        })()}
      </SummaryCard>

      <SummaryCard
        title="Steps"
        titleElement={
          <Button size="small" icon={<IconPlus />} onClick={() => navigate(`/sequences/${sequenceId}/steps/new/email`)}>
            Add step
          </Button>
        }
      >
        {steps.length === 0 ? (
          <Text type="secondary">No steps yet — add the first email in this journey.</Text>
        ) : (
          steps.map((step, i) => (
            <div key={step.id} style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--color-border-2)" }}>
              <div style={{ width: 120 }}>
                <Text type="secondary">Step {i + 1}</Text><br />
                <Text style={{ fontSize: 12 }}>{formatDelay(step.delay_minutes)}</Text>
              </div>
              <div style={{ flexGrow: 1 }}>
                <Text style={{ fontWeight: 500 }}>{step.mail_subject_template || "(no subject)"}</Text>
                {step.should_run === false && <Tag size="small" color="gray" style={{ marginLeft: 8 }}>Paused</Tag>}
              </div>
              <Button size="small" type="text" icon={<IconEdit />} onClick={() => navigate(`/sequences/${sequenceId}/steps/${step.id}/email`)} />
              <Button size="small" type="text" status="danger" icon={<IconDelete />} onClick={() => deleteStep(step)} />
            </div>
          ))
        )}
      </SummaryCard>
    </Content>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);
